import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { adminPost } from "../services/api";
import { WrenchRegular, ArrowClockwiseRegular, DatabaseRegular, ImageRegular, SparkleRegular } from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const Wrench = fluentIcon(WrenchRegular);
const RefreshCw = fluentIcon(ArrowClockwiseRegular);
const Database = fluentIcon(DatabaseRegular);
const Image = fluentIcon(ImageRegular);
const Sparkles = fluentIcon(SparkleRegular);

// governance.md §6: "No write actions without confirmation. Backfill /
// delete buttons must show a typed-confirmation modal." A plain
// window.confirm() was here before (2026-08-01 audit) and, worse, none of
// the tool cards below actually passed destructive={true} to trigger it —
// every one of these ran immediately on a single click. Typing the tool's
// own title is the confirmation; the Confirm button stays disabled until it
// matches exactly.
function ConfirmModal({
  title,
  onConfirm,
  onCancel,
}: {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim() === title;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="card w-full max-w-sm">
        <div className="text-sm font-semibold text-slate-100 mb-2">Confirm: {title}</div>
        <p className="text-xs text-slate-400 mb-4">
          This runs a real write operation against production. Type the tool name below to confirm.
        </p>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={title}
          className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 mb-4 focus:outline-none focus:border-brand-blue"
        />
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches}
            className="px-3 py-2 rounded-lg text-sm font-medium bg-red-900/40 border border-red-900 text-red-300 hover:bg-red-900/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Run it
          </button>
        </div>
      </div>
    </div>
  );
}

function ToolCard({
  icon: Icon,
  title,
  description,
  action,
  destructive = false,
}: {
  icon: any;
  title: string;
  description: string;
  action: () => Promise<string>;
  destructive?: boolean;
}) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [confirming, setConfirming] = useState(false);

  const execute = async () => {
    setConfirming(false);
    setRunning(true);
    setResult(null);
    try {
      const msg = await action();
      setResult({ ok: true, msg });
    } catch (err) {
      setResult({ ok: false, msg: (err as Error).message });
    } finally {
      setRunning(false);
    }
  };

  const run = () => {
    if (destructive) {
      setConfirming(true);
      return;
    }
    execute();
  };

  return (
    <div className="card">
      <div className="flex items-start gap-3 mb-3">
        <Icon size={18} className="text-brand-blue mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-sm">{title}</div>
          <div className="text-xs text-slate-500 mt-1">{description}</div>
        </div>
      </div>
      <button
        onClick={run}
        disabled={running}
        className={`w-full py-2 rounded-lg text-sm font-medium ${
          destructive
            ? "bg-red-900/30 border border-red-900 hover:bg-red-900/50 text-red-300"
            : "bg-brand-blue hover:bg-brand-blue/80"
        } disabled:opacity-50 transition-colors`}
      >
        {running ? "Running…" : "Run"}
      </button>
      {result && (
        <div
          className={`mt-3 text-xs font-mono p-2 rounded ${
            result.ok ? "bg-emerald-950 text-emerald-300" : "bg-red-950 text-red-300"
          }`}
        >
          {result.msg}
        </div>
      )}
      {confirming && (
        <ConfirmModal title={title} onConfirm={execute} onCancel={() => setConfirming(false)} />
      )}
    </div>
  );
}

export default function ToolsPage() {
  const { token } = useAuth();

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold">Tools</h1>
        <p className="text-sm text-slate-500 mt-1">Manual operations. Use with care.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <ToolCard
          icon={RefreshCw}
          title="Trigger Ingestion"
          description="Run news ingestion now instead of waiting for the 30-min cron (or the 10-min-offset RSS-only passes)."
          destructive
          action={async () => {
            const r = await adminPost<any>("/api/v1/admin/ingest", {}, token);
            return r.message ?? `Ingested: ${r.ingested ?? 0}, skipped: ${r.skipped ?? 0}`;
          }}
        />
        <ToolCard
          icon={Database}
          title="Backfill Hindi translations"
          description="Translate a batch of 20 articles missing Hindi. Runs in ~3 min."
          destructive
          action={async () => {
            const r = await adminPost<any>(
              "/api/v1/admin/backfill-translations",
              { lang: "hi", limit: 20 },
              token
            );
            return r.message ?? `Translated: ${r.translated}, remaining: ${r.remaining}`;
          }}
        />
        <ToolCard
          icon={Wrench}
          title="Backfill article locations"
          description="Re-extract location (city, state, lat/lng) for articles missing GPS coords."
          destructive
          action={async () => {
            const r = await adminPost<any>("/api/v1/admin/backfill-locations", {}, token);
            return r.message ?? `Updated: ${r.updated}, skipped: ${r.skipped}`;
          }}
        />
        <ToolCard
          icon={Sparkles}
          title="Re-summarize Short Articles"
          description="AI re-summarize articles with short summaries or long titles (batch of 50)."
          destructive
          action={async () => {
            const r = await adminPost<any>("/api/v1/admin/backfill-ai-summaries", {}, token);
            return r.message ?? `Updated: ${r.updated ?? 0}`;
          }}
        />
        <ToolCard
          icon={Image}
          title="Backfill Article Images"
          description="Fetch og:image for articles missing images."
          destructive
          action={async () => {
            const r = await adminPost<any>("/api/v1/admin/backfill-images", {}, token);
            return r.message ?? `Updated: ${r.updated ?? 0}`;
          }}
        />
      </div>
    </div>
  );
}
